import { useState } from "react";

/**
 * ProfileAvatar
 *
 * Shows a photo when available; falls back to a stable colored-initial circle.
 * Eliminates the onError → src = "/default-avatar.png" → onError infinite loop
 * that causes the blinking/flickering effect.
 *
 * Props:
 *   photoUrl  – string | null | undefined  — absolute photo URL
 *   name      – string                      — used to derive initials & bg colour
 *   size      – number (px)                 — diameter, default 144
 *   className – extra Tailwind / CSS classes on the wrapper
 */

const PALETTE = [
  ["#dbeafe", "#1d4ed8"], // blue
  ["#dcfce7", "#15803d"], // green
  ["#fce7f3", "#be185d"], // pink
  ["#ede9fe", "#6d28d9"], // purple
  ["#ffedd5", "#c2410c"], // orange
  ["#cffafe", "#0e7490"], // cyan
  ["#fef9c3", "#854d0e"], // yellow
];

function getColor(name = "") {
  // Deterministic colour from the name so it never changes on re-render
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return PALETTE[Math.abs(hash) % PALETTE.length];
}

function getInitials(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const ProfileAvatar = ({ photoUrl, name = "", size = 144, className = "" }) => {
  const [imgFailed, setImgFailed] = useState(false);

  const showInitials = !photoUrl || imgFailed;
  const [bg, fg] = getColor(name);
  const initials = getInitials(name);

  const baseStyle = {
    width: size,
    height: size,
    borderRadius: "50%",
    overflow: "hidden",
    flexShrink: 0,
    border: "2px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  if (showInitials) {
    return (
      <div
        className={className}
        style={{
          ...baseStyle,
          background: bg,
          color: fg,
          fontSize: size * 0.28,
          fontWeight: 700,
          letterSpacing: "0.04em",
          userSelect: "none",
        }}
        aria-label={`Avatar for ${name}`}
      >
        {initials}
      </div>
    );
  }

  return (
    <img
      src={photoUrl}
      alt={name || "Profile"}
      className={className}
      style={{ ...baseStyle, objectFit: "cover" }}
      onError={() => setImgFailed(true)} // one-shot: switch to initials, never loops
    />
  );
};

export default ProfileAvatar;
